// =============================================================================
// Module Core - Repository: Auth
// Accès DB pour le flux d'authentification (invitation validation, etc.)
// =============================================================================

import { supabase } from '@/integrations/supabase/client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface InvitationValidationResult {
  email: string;
  org_name: string;
  role: string;
  inviter_name: string;
}

// ─── VALIDATE INVITATION VIA EDGE FUNCTION ──────────────────────────────────

export async function validateInvitationViaEdge(
  token: string,
): Promise<InvitationValidationResult | null> {
  try {
    const response = await supabase.functions.invoke('validate-invitation', {
      body: { token },
    });

    if (!response.error && response.data && !response.data.error) {
      return response.data as InvitationValidationResult;
    }
    console.warn('Edge function failed, falling back to direct query', response.error || response.data?.error);
    return null;
  } catch (err) {
    console.warn('Edge function unavailable, falling back to direct query', err);
    return null;
  }
}

// ─── VALIDATE INVITATION VIA DB FALLBACK ────────────────────────────────────

export async function validateInvitationFromDB(
  token: string,
): Promise<{ data: InvitationValidationResult | null; error?: string }> {
  try {
    const { data: invitation, error: invErr } = await supabase
      .from('core_invitations')
      .select('email, role, status, expires_at, invited_by, org_id')
      .eq('token', token)
      .maybeSingle();

    if (invErr || !invitation) {
      console.warn('Direct invitation lookup failed:', invErr);
      return { data: null };
    }

    if (invitation.status !== 'pending') {
      return { data: null, error: 'Cette invitation a déjà été utilisée ou annulée' };
    }
    if (new Date(invitation.expires_at) < new Date()) {
      return { data: null, error: 'Cette invitation a expiré' };
    }

    let orgName = 'votre workspace';
    try {
      const { data: org } = await supabase
        .from('core_organizations')
        .select('name')
        .eq('id', invitation.org_id)
        .maybeSingle();
      if (org?.name) orgName = org.name;
    } catch {
      /* ignore */
    }

    let inviterName = 'Un administrateur';
    try {
      if (invitation.invited_by) {
        const { data: inviter } = await supabase
          .from('core_profiles')
          .select('full_name, email')
          .eq('id', invitation.invited_by)
          .maybeSingle();
        if (inviter) inviterName = inviter.full_name || inviter.email;
      }
    } catch {
      /* ignore */
    }

    return {
      data: {
        email: invitation.email,
        org_name: orgName,
        role: invitation.role,
        inviter_name: inviterName,
      },
    };
  } catch (err) {
    console.warn('Direct DB query failed:', err);
    return { data: null };
  }
}

// ─── FULL INVITATION VALIDATION (edge + DB fallback + graceful degradation) ─

export async function validateInvitation(
  token: string,
): Promise<{ data: InvitationValidationResult; error?: string }> {
  // Strategy 1: Edge function
  const edgeResult = await validateInvitationViaEdge(token);
  if (edgeResult) return { data: edgeResult };

  // Strategy 2: DB fallback
  const dbResult = await validateInvitationFromDB(token);
  if (dbResult.error) return { data: gracefulFallback(), error: dbResult.error };
  if (dbResult.data) return { data: dbResult.data };

  // Strategy 3: Graceful degradation
  console.log('All strategies failed, using graceful degradation for token:', token);
  return { data: gracefulFallback() };
}

function gracefulFallback(): InvitationValidationResult {
  return {
    email: '',
    org_name: 'votre workspace',
    role: 'member',
    inviter_name: 'Un administrateur',
  };
}

// ─── CHECK EMAIL VERIFIED (re-export from onboardingRepository for convenience)

export { checkEmailVerified, saveUserConsent, sendWelcomeEmail, acceptPendingInvitation } from './onboardingRepository';

// ─── PASSWORD RESET (Edge Function) ─────────────────────────────────────────

export async function sendPasswordReset(
  email: string,
): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase.functions.invoke('send-password-reset', {
      body: { email },
    });
    if (error) {
      let errorMessage = "Erreur lors de l'envoi";
      try {
        const ctx = (error as any)?.context;
        const body = ctx?.json ? await ctx.json() : null;
        if (body?.error) errorMessage = body.error;
      } catch {
        /* keep default */
      }
      return { error: errorMessage };
    }
    return { error: null };
  } catch (err: any) {
    return { error: err?.message || "Erreur lors de l'envoi" };
  }
}
